import type { ReactNode } from 'react';
import { cx } from '../cx';
import { VisuallyHidden } from '../VisuallyHidden';

export type FigureStripItem = {
  id: string;
  /** What the figure counts ("new this week"). */
  label: ReactNode;
  /** Already locale-formatted (formatNumber, formatMoney). */
  value: ReactNode;
  /** Makes the value a link (a pay figure that opens the salary page). */
  href?: string;
  /** What the figure means. Read by screen readers after the label. */
  meaning?: ReactNode;
  /** Where the figure comes from and when. Read by screen readers after the label. */
  source?: ReactNode;
};

export type FigureStripProps = {
  items: FigureStripItem[];
  className?: string;
};

/**
 * A few figures in one line: the value in ink, its label beside it, a hairline between. A compact
 * StatTile for a page that must stay short (search results). On a phone the figures become a small
 * grid, each value above its label, so nothing runs off the screen (a sideways scroll read as broken).
 * The meaning and source are kept for screen readers, so the basis of a figure is never lost.
 */
export function FigureStrip({ items, className }: FigureStripProps) {
  if (items.length === 0) return null;
  return (
    <ul
      className={cx(
        // Phones: packed from the left (value over label), wrapping, instead of fixed columns that
        // spread two figures across the whole width. From sm: one line with dividers.
        'm-0 flex min-w-0 list-none flex-wrap gap-x-6 gap-y-2 p-0 sm:items-center sm:gap-x-3 sm:gap-y-1',
        className,
      )}
    >
      {items.map((item, index) => (
        <li
          key={item.id}
          className={cx(
            'flex min-w-0 flex-col sm:flex-row sm:items-baseline sm:gap-1.5 sm:whitespace-nowrap',
            index > 0 && 'sm:border-l sm:border-jc-border sm:pl-3',
          )}
        >
          <span className="text-sm font-semibold tabular-nums text-jc-ink">
            {item.href ? (
              <a
                href={item.href}
                className="rounded-jc-info underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
              >
                {item.value}
              </a>
            ) : (
              item.value
            )}
          </span>
          <span className="type-caption text-jc-ink-secondary">{item.label}</span>
          {item.meaning || item.source ? (
            <VisuallyHidden>
              {item.meaning ? <>, {item.meaning}</> : null}
              {item.source ? <>, {item.source}</> : null}
            </VisuallyHidden>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
